import { useEffect, useRef, useState, type ReactNode } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

import { Textarea } from "@/components/ui/textarea";
import { CalendarClock, ChevronDown } from "lucide-react";
import { formatMeetingLocal, nextStepLabel } from "@/lib/meeting-time";
import { useOwnerTimezone } from "@/hooks/use-owner-timezone";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getDealContact,
  updateContactStatus,
  addContactStatusNote,
  listDealContacts,
  type DealContactRow,
  type InvestorStatus,
  type StatusNote,
} from "@/lib/contacts.functions";
import { getDealDetail } from "@/lib/deals.functions";
import { InitialOutreach } from "@/components/deal-contacts/initial-outreach";
import { FollowupSection } from "@/components/deal-contacts/followup-section";
import { SignalSection, type SignalItem, type SignalSectionHandle } from "@/components/deal-contacts/signal-section";
import { TagsEditor } from "@/components/deal-contacts/tags-editor";
import { StatusNotesList } from "@/components/deal-contacts/status-notes-list";
import {
  STATUS_ORDER,
  STATUS_LABEL,
  STATUS_COLOR,
} from "@/components/deal-contacts/status-meta";
import { cn } from "@/lib/utils";
import { track } from "@/lib/track";
import { canonicalizeInvestorType } from "@/lib/investor-types";
import { humanizePropertyTypes, humanizePropertyTypesInText } from "@/lib/property-types";
import { FirmWarmIntroPaths } from "@/components/deal-contacts/firm-warm-intro-paths";
import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { MeetingBlock } from "@/components/deal-contacts/meeting-block";
import { useOwnerMeetingProfile } from "@/hooks/use-owner-meeting-profile";

const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "aol.com",
  "icloud.com",
]);

function resolveFirmWebsite(
  companyDomain: string | null | undefined,
  email: string | null | undefined,
): { href: string; label: string } | null {
  const normalize = (d: string) => {
    const cleaned = d.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, "");
    return cleaned || null;
  };
  let domain = companyDomain ? normalize(companyDomain) : null;
  if (!domain && email) {
    const at = email.lastIndexOf("@");
    if (at >= 0) {
      const fromEmail = normalize(email.slice(at + 1));
      if (fromEmail && !FREE_EMAIL_DOMAINS.has(fromEmail)) domain = fromEmail;
    }
  }
  if (!domain) return null;
  return { href: `https://${domain}`, label: domain };
}

function normalizeLinkedInUrl(url: string | null | undefined): string | null {
  if (!url || !url.trim()) return null;
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}


export const Route = createFileRoute("/_authed/deals/$dealId_/investors/$contactId")({
  head: () => ({
    meta: [
      { title: "Contact: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ContactDetailPage,
  errorComponent: ContactNotFoundBoundary,
});

function ContactNotFoundBoundary() {
  const router = useRouter();
  return (
    <main className="mx-auto max-w-3xl px-6 py-10 space-y-4">
      <h1 className="font-serif text-2xl text-brand-ink">Contact not found</h1>
      <p className="text-sm text-brand-muted">
        This contact doesn&rsquo;t exist or you don&rsquo;t have access to it.
      </p>
      <Button variant="outline" onClick={() => router.navigate({ to: "/dashboard" })}>
        Back to dashboard
      </Button>
    </main>
  );
}

function ContactDetailPage() {
  const { dealId, contactId } = Route.useParams();
  const ownerTz = useOwnerTimezone();
  const ownerProfile = useOwnerMeetingProfile();
  const [signalItems, setSignalItems] = useState<SignalItem[]>([]);
  const [selectedSignalIdx, setSelectedSignalIdx] = useState<number | null>(null);
  const signalRef = useRef<SignalSectionHandle | null>(null);
  const fetchContact = useServerFn(getDealContact);
  const fetchList = useServerFn(listDealContacts);
  const fetchDetail = useServerFn(getDealDetail);
  const detail = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDetail({ data: { dealId } }),
  });
  const q = useQuery({
    queryKey: ["deal-contact", dealId, contactId],
    queryFn: () => fetchContact({ data: { dealId, contactId } }),
  });
  // Whole-deal pager: list every contact on the deal regardless of
  // source_run_id so prev/next spans pipeline + imported contacts. Lazily
  // paginated at 200/page so >1000-contact deals don't blow the payload;
  // we auto-fetch the next page when the current contact approaches the
  // end of loaded rows (or isn't loaded yet).
  const NAV_PAGE_SIZE = 200;
  const listQ = useInfiniteQuery({
    queryKey: ["deal-contacts-nav", dealId],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchList({
        data: {
          dealId,
          sortBy: "rating_rank",
          sortDir: "desc",
          limit: NAV_PAGE_SIZE,
          offset: pageParam as number,
        },
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.rows.length, 0);
      return loaded < (lastPage.total ?? 0) ? loaded : undefined;
    },
    staleTime: 60_000,
  });

  // Stable deal-level rating rank. Mirrors the firmRankQuery in
  // contacts-section.tsx so the rank shown here matches the #N chip on the
  // list for the same firm. Fund-only; computed once per deal.
  const firmRankQuery = useQuery({
    queryKey: ["deal-firm-rank", dealId],
    queryFn: async () => {
      // Page over PostgREST's 1000-row cap so >1000-firm deals still get
      // a complete rank map.
      type Row = {
        id: string;
        rating_rank: number | null;
        created_at: string | null;
        company_name: string | null;
      };
      const PAGE = 1000;
      const MAX = 5000;
      const rows: Row[] = [];
      for (let from = 0; from < MAX; from += PAGE) {
        const to = Math.min(from + PAGE - 1, MAX - 1);
        const { data, error } = await supabase
          .from("deal_investors")
          .select("id, rating_rank, created_at, company_name")
          .eq("deal_id", dealId)
          .range(from, to);
        if (error) throw new Error(error.message);
        const page = (data ?? []) as Row[];
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      const sorted = [...rows].sort((a, b) => {
        const ar = a.rating_rank ?? -Infinity;
        const br = b.rating_rank ?? -Infinity;
        if (br !== ar) return br - ar;
        const at = a.created_at
          ? Date.parse(a.created_at)
          : Number.MAX_SAFE_INTEGER;
        const bt = b.created_at
          ? Date.parse(b.created_at)
          : Number.MAX_SAFE_INTEGER;
        if (at !== bt) return at - bt;
        return (a.company_name ?? "").localeCompare(b.company_name ?? "");
      });
      const map = new Map<string, number>();
      sorted.forEach((r, i) => map.set(r.id, i + 1));
      return map;
    },
  });

  // Compute nav rows/idx BEFORE any early return so the auto-fetch effect
  // always runs in the same order (rules of hooks).
  const navRowsAll = (listQ.data?.pages ?? []).flatMap((p) => p.rows);
  const navIdx = navRowsAll.findIndex((r) => r.id === contactId);
  useEffect(() => {
    if (!listQ.hasNextPage || listQ.isFetchingNextPage) return;
    if (navIdx === -1 || navIdx >= navRowsAll.length - 20) {
      void listQ.fetchNextPage();
    }
  }, [navIdx, navRowsAll.length, listQ.hasNextPage, listQ.isFetchingNextPage, listQ]);

  if (q.isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-10 text-sm text-brand-muted">
        Loading…
      </main>
    );
  }
  if (q.error || !q.data) return <ContactNotFoundBoundary />;

  const contact: DealContactRow = q.data;
  const inv = contact.investor;
  const isFund =
    (contact.deal?.plan_json as Record<string, unknown> | null)?.branch ===
    "Fund";
  const firmRankN = isFund
    ? firmRankQuery.data?.get(inv.id) ?? null
    : null;
  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—";
  const aumLabel = formatAum(inv.aum_usd_mm);
  const investorType = canonicalizeInvestorType(inv.investor_type);
  const identityParts = [
    contact.title,
    investorType || null,
  ].filter((v): v is string => Boolean(v && v.trim()));

  const rows = navRowsAll;
  const navTotal = listQ.data?.pages[0]?.total ?? rows.length;
  const idx = navIdx;
  const prevContact = idx > 0 ? rows[idx - 1] : null;
  const nextContact = idx >= 0 && idx < rows.length - 1 ? rows[idx + 1] : null;
  const positionLabel = idx >= 0 ? `${idx + 1} of ${navTotal}` : null;

  return (
    <main className="mx-auto max-w-[1400px] space-y-6 px-6 py-10">
      <DealPageHeader dealId={dealId} />
      <DealNav dealId={dealId} active="contacts" isDemo={(detail.data?.deal as { is_demo?: boolean } | undefined)?.is_demo === true} />
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-2 text-sm text-brand-muted">
          <Link
            to="/deals/$dealId/contacts"
            params={{ dealId }}
            className="no-underline hover:text-brand-ink"
          >
            Contacts
          </Link>
          <span aria-hidden>›</span>
          <span className="truncate text-brand-ink">{fullName}</span>
          {inv.company_name && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate text-brand-ink">{inv.company_name}</span>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-brand-muted">
          {prevContact ? (
            <Link
              to="/deals/$dealId/investors/$contactId"
              params={{ dealId, contactId: prevContact.id }}
              className="no-underline hover:text-brand-ink"
            >
              ← Previous contact
            </Link>
          ) : (
            <span className="cursor-not-allowed opacity-40">← Previous contact</span>
          )}
          {positionLabel && <span>{positionLabel}</span>}
          {nextContact ? (
            <Link
              to="/deals/$dealId/investors/$contactId"
              params={{ dealId, contactId: nextContact.id }}
              className="no-underline hover:text-brand-ink"
            >
              Next contact →
            </Link>
          ) : (
            <span className="cursor-not-allowed opacity-40">Next contact →</span>
          )}
        </div>
      </div>
      <header className="space-y-4">
        <div>
          <h1 className="flex flex-wrap items-center gap-x-3 gap-y-2 font-serif text-3xl text-brand-ink">
            <span>{fullName}</span>
            {inv.company_name && (
              <>
                <span aria-hidden className="text-brand-line">·</span>
                <span>{inv.company_name}</span>
              </>
            )}
            <span aria-hidden className="text-brand-line">·</span>
            <StatusDropdown dealId={dealId} contact={contact} prominent />
            <CopyLinkButton />
          </h1>
          <IdentityAndMethodsLine
            identityParts={identityParts}
            contact={contact}
          />
          {contact.introducer_name && (contact.warm_intro || contact.referral_note) ? (
            <div className="mt-1 text-[13px] text-brand-ink-2">
              {contact.warm_intro ? "Introduced by" : "Referred by"}{" "}
              {contact.introducer_name}
              {contact.introducer_company ? `, ${contact.introducer_company}` : ""}
            </div>
          ) : null}
          {contact.next_step_at && (
            <div
              className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-brand-wash px-2 py-1 text-[13px] text-brand-ink"
              title={contact.next_step_note ?? undefined}
            >
              <CalendarClock aria-hidden className="h-3.5 w-3.5 text-brand-muted" />
              <span>
                Next: {nextStepLabel(contact.next_step_kind)}{" "}
                {formatMeetingLocal(contact.next_step_at, ownerTz)}
              </span>
            </div>
          )}
          <MeetingBlock
            dealId={dealId}
            contact={contact}
            timezone={ownerTz}
            meetingKind={ownerProfile?.meeting_link_kind}
          />
        </div>

        {(contact.why_this_contact || contact.rationale) && (
          <section className="space-y-1">
            <div className="text-[12px] font-medium uppercase tracking-wider text-brand-muted">
              Why this contact
            </div>
            <p className="text-[14px] leading-relaxed text-brand-ink-2">
              {humanizePropertyTypesInText(contact.why_this_contact ?? contact.rationale ?? "")}
            </p>
          </section>
        )}

        <hr className="border-t border-brand-line" />
        <div className="flex flex-wrap items-start gap-x-6 gap-y-3 text-[14px]">
          {isFund ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[12px] font-medium text-brand-muted">
                RANK
              </span>
              <span className="font-medium text-brand-ink">
                {firmRankN != null ? `#${firmRankN}` : "—"}
              </span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[12px] font-medium text-brand-muted">
                RATING
              </span>
              <span className="font-medium text-brand-ink">
                {inv.rating_rank ?? "—"}
              </span>
            </span>
          )}
          <TagsEditor
            dealId={dealId}
            contactId={contact.id}
            tags={contact.tags}
            label={<span className="text-[12px] font-medium text-brand-muted">TAGS</span>}
          />
        </div>
        {!isFund && inv.rating_rationale && (
          <div className="mt-1 text-[14px] leading-relaxed text-brand-ink-2">
            <span className="text-brand-muted">Why this rating — </span>
            {inv.rating_rationale}
          </div>
        )}
      </header>

      <hr className="border-t border-brand-line" />

      {/* Three-column body */}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1fr)]">
        {/* Column 1 — Notes */}
        <div className="flex flex-col gap-3">
          <ColumnHeading>Your Notes</ColumnHeading>
          <NotesPanel
            dealId={dealId}
            contactId={contact.id}
            notes={
              Array.isArray(contact.status_notes)
                ? (contact.status_notes as StatusNote[])
                : []
            }
          />
        </div>

        {/* Column 2 — Messaging */}
        <div className="flex flex-col gap-3 md:col-span-2 xl:col-span-1">
          <ColumnHeading>Messaging</ColumnHeading>
          <div className="space-y-6">
            <InitialOutreach dealId={dealId} contact={contact} />
            <SignalSection
              ref={signalRef}
              dealId={dealId}
              contactId={contact.id}
              onItemsChange={setSignalItems}
              selectedIndex={selectedSignalIdx}
              onSelectedChange={setSelectedSignalIdx}
            />
            <FollowupSection
              dealId={dealId}
              contact={contact}
              selectedSignal={
                selectedSignalIdx != null ? (signalItems[selectedSignalIdx] ?? null) : null
              }
              onFindSignal={() => signalRef.current?.fetchSignal()}
            />
            <FirmWarmIntroPaths
              dealId={dealId}
              domain={inv.company_domain ?? null}
              asSection
              contactId={contact.id}
              contactLinkedinUrl={contact.linkedin_url}
              introducerName={contact.introducer_name}
            />
          </div>
        </div>

        {/* Column 3 — Investor Information */}
        <div className="flex flex-col gap-3">
          <ColumnHeading>Investor Information</ColumnHeading>
          <div className="space-y-6">
          <section className="space-y-2 rounded-xl border border-brand-line bg-brand-paper p-4">
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-brand-ink">{inv.company_name}</span>
                {inv.rating_rank == null && (
                  <span
                    title="Surfaced via plan-tag matching, not the initial investor list."
                    className="rounded-sm border border-brand-line bg-brand-paper-2 px-1 text-[12px] text-brand-muted"
                  >
                    Supplemental
                  </span>
                )}
              </div>
              {investorType && (
                <div className="text-[12px] text-brand-muted">
                  {investorType}
                </div>
              )}
            </div>
            <dl className="mt-2 grid [grid-template-columns:max-content_1fr] gap-x-4 gap-y-1 text-[14px]">
              {(() => {
                const site = resolveFirmWebsite(inv.company_domain, contact.email);
                if (!site) return null;
                return (
                  <Pair
                    k="Website"
                    v={
                      <a
                        href={site.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-ink underline underline-offset-2 hover:text-brand-accent"
                      >
                        {site.label}
                      </a>
                    }
                  />
                );
              })()}
              {(() => {
                const linkedIn = normalizeLinkedInUrl(inv.linkedin_url_company);
                if (!linkedIn) return null;
                return (
                  <Pair
                    k="Company LinkedIn"
                    v={
                      <a
                        href={linkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-ink underline underline-offset-2 hover:text-brand-accent"
                      >
                        View on LinkedIn
                      </a>
                    }
                  />
                );
              })()}
              {inv.in_directory && (
                <>
                  {aumLabel && <Pair k="AUM" v={aumLabel} />}
                  {(inv.city || inv.state || inv.country) && (
                    <Pair k="Location" v={[inv.city, inv.state, inv.country].filter(Boolean).join(", ")} />
                  )}
                  {inv.is_strategic != null && (
                    <Pair k="Strategic" v={inv.is_strategic ? "Yes" : "No"} />
                  )}
                  {inv.investments_past_2yr != null && (
                    <Pair k="# of Investments made - 2yr" v={String(inv.investments_past_2yr)} />
                  )}
                  {inv.investment_professionals != null && (
                    <Pair k="Professionals" v={String(inv.investment_professionals)} />
                  )}
                </>
              )}
            </dl>
            {inv.company_description && (
              <p className="mt-2 text-[14px] leading-relaxed text-brand-ink-2">
                {inv.company_description}
              </p>
            )}
          </section>

          <section className="space-y-2 rounded-xl border border-brand-line bg-brand-paper p-4">
            <div className="text-[12px] font-medium uppercase tracking-wider text-brand-muted">
              Investment criteria
            </div>
            {(() => {
              const rawBranch = (contact.deal?.plan_json as Record<string, unknown> | null)?.branch;
              const branch = typeof rawBranch === "string" ? rawBranch.trim() : "";
              const isLoansDebt = branch === "Loans/Debt" || branch === "Debt";
              return (
            <>
            {branch === "RealEstate" ? (
              <>
                <PrefList label="Property types" items={inv.pref_real_estate_property} humanize />
                <PrefList label="Capital type" items={inv.pref_real_estate_equity_debt} />
                <PrefList label="Geo" items={inv.pref_location} />
                <PrefList label="Check size" items={inv.pref_invest_size} />
                {!inv.pref_real_estate_property?.length &&
                  !inv.pref_real_estate_equity_debt?.length &&
                  !inv.pref_location?.length &&
                  !inv.pref_invest_size?.length && (
                    <p className="text-[14px] text-brand-muted">
                      No criteria on file.
                    </p>
                  )}
              </>
            ) : branch === "Fund" ? (
              <>
                {canonicalizeInvestorType(inv.investor_type) ? (
                  <PrefList label="Investor type" items={[canonicalizeInvestorType(inv.investor_type)]} />
                ) : null}
                <PrefList label="Geo" items={inv.pref_location} />
                <PrefList label="Check size" items={inv.pref_invest_size} />
                <PrefList label="Investments in Funds" items={inv.pref_investments_in_funds} />
                {!canonicalizeInvestorType(inv.investor_type) &&
                  !inv.pref_location?.length &&
                  !inv.pref_invest_size?.length &&
                  !inv.pref_investments_in_funds?.length && (
                    <p className="text-[14px] text-brand-muted">
                      No criteria on file.
                    </p>
                  )}
              </>
            ) : isLoansDebt ? (
              <>
                <PrefList label="Loan Types" items={inv.pref_loan_types} />
                <PrefList label="Facility Size" items={inv.pref_invest_size} />
                <PrefList label="Geography" items={inv.pref_location} />
                {!inv.pref_loan_types?.length &&
                  !inv.pref_invest_size?.length &&
                  !inv.pref_location?.length &&
                  inv.pref_other?.length ? (
                    <PrefList label="Other" items={inv.pref_other} />
                  ) : null}
                {!inv.pref_loan_types?.length &&
                  !inv.pref_invest_size?.length &&
                  !inv.pref_location?.length &&
                  !inv.pref_other?.length && (
                    <p className="text-[14px] text-brand-muted">
                      No criteria on file.
                    </p>
                  )}
              </>
            ) : (
              <>
                <PrefList label="Stage" items={inv.pref_stage} />
                <PrefList label="Industries" items={inv.pref_industry_types} />
                <PrefList label="Companies" items={inv.pref_company_types} />
                <PrefList label="Verticals" items={inv.pref_vertical_types} />
                <PrefList label="Geo" items={inv.pref_location} />
                <PrefList label="Check size" items={inv.pref_invest_size} />
                {!inv.pref_stage?.length &&
                  !inv.pref_industry_types?.length &&
                  !inv.pref_company_types?.length &&
                  !inv.pref_vertical_types?.length &&
                  !inv.pref_location?.length &&
                  !inv.pref_invest_size?.length && (
                    <p className="text-[14px] text-brand-muted">
                      No criteria on file.
                    </p>
                  )}
              </>
            )}
            </>
              );
            })()}
          </section>

          </div>
        </div>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* Status dropdown — shows current status with menu to change.         */
/* ------------------------------------------------------------------ */
function StatusDropdown({
  dealId,
  contact,
  prominent = false,
}: {
  dealId: string;
  contact: DealContactRow;
  prominent?: boolean;
}) {
  const qc = useQueryClient();
  const setStatus = useServerFn(updateContactStatus);
  const addNote = useServerFn(addContactStatusNote);
  const [local, setLocal] = useState<InvestorStatus>(contact.investor_status);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLocal(contact.investor_status);
  }, [contact.id, contact.investor_status]);

  async function pick(next: InvestorStatus) {
    if (busy || next === local) return;
    const prev = local;
    setLocal(next);
    setBusy(true);
    try {
      await setStatus({ data: { dealId, contactId: contact.id, status: next } });
      track(
        "contact_status_changed",
        { from: prev, to: next },
        { deal_id: dealId },
      );
      // Auto-log a note describing the change, same as email/LinkedIn sends.
      try {
        await addNote({
          data: {
            dealId,
            contactId: contact.id,
            text: `Status changed to ${STATUS_LABEL[next]}`,
            kind: "manual",
          },
        });
      } catch {
        // Note logging is best-effort; never block the status update.
      }
      await qc.invalidateQueries({
        queryKey: ["deal-contact", dealId, contact.id],
      });
      await qc.invalidateQueries({ queryKey: ["deal-contacts", dealId] });
    } catch (e) {
      setLocal(prev);
      toast.error(e instanceof Error ? e.message : "Failed to update status");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={busy}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors hover:opacity-90 disabled:opacity-60",
            prominent
              ? "px-4 py-1.5 text-[18px]"
              : "px-3 py-1 text-[12px]",
            STATUS_COLOR[local],
          )}
          aria-label="Change status"
        >
          {STATUS_LABEL[local]}
          <ChevronDown className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]">
        {STATUS_ORDER.map((s) => (
          <DropdownMenuItem
            key={s}
            onSelect={() => void pick(s)}
            className="flex items-center gap-2"
          >
            <span
              className={cn(
                "inline-block size-2 rounded-full border",
                STATUS_COLOR[s],
              )}
            />
            <span className={cn(s === local && "font-semibold")}>
              {STATUS_LABEL[s]}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ------------------------------------------------------------------ */
/* Notes panel — manual entry + history (edit/delete via list).        */
/* ------------------------------------------------------------------ */
function NotesPanel({
  dealId,
  contactId,
  notes,
}: {
  dealId: string;
  contactId: string;
  notes: StatusNote[];
}) {
  const qc = useQueryClient();
  const addNote = useServerFn(addContactStatusNote);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      await addNote({
        data: { dealId, contactId, text, kind: "manual" },
      });
      setDraft("");
      await qc.invalidateQueries({
        queryKey: ["deal-contact", dealId, contactId],
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add note");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-2 rounded-xl border border-brand-line bg-brand-paper p-4">
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        placeholder="Log a note (visible only to your team)…"
        maxLength={4000}
      />
      <div className="flex justify-end">
        <Button size="sm" disabled={busy || !draft.trim()} onClick={() => void submit()}>
          Add note
        </Button>
      </div>
      <StatusNotesList dealId={dealId} contactId={contactId} notes={notes} />
    </section>
  );
}

function formatAum(v: number | null | undefined): string | null {
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < 1000) return `$${Math.round(n)}mm`;
  return `$${(n / 1000).toFixed(1)}B`;
}

function Pair({ k, v }: { k: string; v: ReactNode }) {
  return (
    <>
      <dt className="text-brand-muted">{k}</dt>
      <dd className="text-brand-ink">{v}</dd>
    </>
  );
}

function ColumnHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-[15px] font-semibold uppercase tracking-wider text-brand-ink">
      {children}
    </h2>
  );
}

function IdentityAndMethodsLine({
  identityParts,
  contact,
}: {
  identityParts: string[];
  contact: DealContactRow;
}) {
  const notes = Array.isArray(contact.status_notes) ? contact.status_notes : [];
  const lastNote = notes.length > 0 ? notes[notes.length - 1] : null;
  const lastContactLabel = lastNote
    ? new Date(lastNote.created_at).toLocaleDateString()
    : "—";

  type Part = { key: string; node: ReactNode };
  const parts: Part[] = [];
  identityParts.forEach((p, i) =>
    parts.push({
      key: `id-${i}`,
      node: <span className="text-brand-ink-2">{p}</span>,
    }),
  );
  if (contact.email) {
    parts.push({
      key: "email",
      node: (
        <a
          href={`mailto:${contact.email}`}
          className="text-brand-accent hover:text-brand-ink"
        >
          {contact.email}
        </a>
      ),
    });
  }
  if (contact.linkedin_url) {
    parts.push({
      key: "linkedin",
      node: (
        <a
          href={contact.linkedin_url}
          target="_blank"
          rel="noreferrer"
          className="text-brand-accent hover:text-brand-ink"
        >
          LinkedIn
        </a>
      ),
    });
  }
  if (contact.phone_company) {
    parts.push({
      key: "phone",
      node: <span className="text-brand-muted">{contact.phone_company}</span>,
    });
  }
  parts.push({
    key: "last",
    node: (
      <span className="text-brand-muted">Last contact: {lastContactLabel}</span>
    ),
  });

  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] text-brand-muted">
      {parts.map((p, i) => (
        <span key={p.key} className="inline-flex items-center gap-x-2">
          {i > 0 && <span aria-hidden>·</span>}
          {p.node}
        </span>
      ))}
    </p>
  );
}
function PrefList({ label, items, humanize = false }: { label: string; items: string[]; humanize?: boolean }) {
  if (!items || items.length === 0) return null;
  const display = humanize ? humanizePropertyTypes(items) : items;
  return (
    <div className="mt-2">
      <div className="text-[14px] uppercase tracking-wider text-brand-muted">{label}</div>
      <div className="mt-0.5 flex flex-wrap gap-1">
        {display.map((it) => (
          <span
            key={it}
            className="rounded-full border border-brand-line bg-brand-paper-2 px-1.5 py-0.5 text-[12px] text-brand-ink-2"
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}
function CopyLinkButton() {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy link");
    }
  }
  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label="Copy link to this contact"
      className="inline-flex items-center gap-1 rounded-md border border-brand-line bg-brand-paper px-2 py-0.5 font-sans text-[12px] font-medium uppercase tracking-wider text-brand-muted hover:text-brand-ink"
    >
      {copied ? "Copied" : "Copy link"}
    </button>
  );
}
